import { Injectable } from '@angular/core';
import type { PlanV2IssueCardPayload } from '../utils/plan-v2-issues';
import type {
  InternalLinkComposeOption,
  TechnicalIssueComposeOption,
  TopicOwnershipComposeState
} from '../utils/plan-v2-work-item-compose';

export interface PlanV2EmbedContextHost {
  enrichWorkItemPayload(payload: PlanV2IssueCardPayload): PlanV2IssueCardPayload;
  beginTopicOwnershipCompose(): boolean;
  endTopicOwnershipCompose(): void;
  getTopicOwnershipComposeState(): TopicOwnershipComposeState | null;
  topicOwnershipComposeHasSelection(): boolean;
  finalizeTopicOwnershipComposeWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload;
  getTechnicalIssueComposeOptions(
    issueType: 'technical_critical' | 'technical_warnings'
  ): TechnicalIssueComposeOption[];
  finalizeTechnicalIssuesWorkItem(
    base: PlanV2IssueCardPayload,
    issueType: 'technical_critical' | 'technical_warnings',
    selected: TechnicalIssueComposeOption[]
  ): PlanV2IssueCardPayload;
  getInternalLinkComposeOptions(): InternalLinkComposeOption[];
  finalizeInternalLinksWorkItem(
    base: PlanV2IssueCardPayload,
    selected: InternalLinkComposeOption[]
  ): PlanV2IssueCardPayload[];
  needsWorkItemCompose(issueType: PlanV2IssueCardPayload['issueType']): boolean;
}

@Injectable({ providedIn: 'root' })
export class PlanV2EmbedContextService {
  private host: PlanV2EmbedContextHost | null = null;

  registerHost(host: PlanV2EmbedContextHost | null): void {
    this.host = host;
  }

  get hasHost(): boolean {
    return Boolean(this.host);
  }

  needsCompose(payload: PlanV2IssueCardPayload): boolean {
    return this.host?.needsWorkItemCompose(payload.issueType) ?? false;
  }

  enrichPayload(payload: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    return this.host?.enrichWorkItemPayload(payload) ?? payload;
  }

  beginTopicOwnershipCompose(): boolean {
    return this.host?.beginTopicOwnershipCompose() ?? false;
  }

  endTopicOwnershipCompose(): void {
    this.host?.endTopicOwnershipCompose();
  }

  getTopicOwnershipComposeState(): TopicOwnershipComposeState | null {
    return this.host?.getTopicOwnershipComposeState() ?? null;
  }

  topicOwnershipComposeHasSelection(): boolean {
    return this.host?.topicOwnershipComposeHasSelection() ?? false;
  }

  finalizeTopicOwnershipFromCompose(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    return this.host?.finalizeTopicOwnershipComposeWorkItem(base) ?? base;
  }

  getTechnicalIssueComposeOptions(
    issueType: 'technical_critical' | 'technical_warnings'
  ): TechnicalIssueComposeOption[] {
    return this.host?.getTechnicalIssueComposeOptions(issueType) ?? [];
  }

  finalizeTechnicalIssues(
    base: PlanV2IssueCardPayload,
    issueType: 'technical_critical' | 'technical_warnings',
    selected: TechnicalIssueComposeOption[]
  ): PlanV2IssueCardPayload {
    return this.host?.finalizeTechnicalIssuesWorkItem(base, issueType, selected) ?? base;
  }

  getInternalLinkComposeOptions(): InternalLinkComposeOption[] {
    return this.host?.getInternalLinkComposeOptions() ?? [];
  }

  finalizeInternalLinks(
    base: PlanV2IssueCardPayload,
    selected: InternalLinkComposeOption[]
  ): PlanV2IssueCardPayload[] {
    return this.host?.finalizeInternalLinksWorkItem(base, selected) ?? [];
  }
}
